<template>
  <article class="employee-teaser">
    <div class="aspect-square">
      <img
        class="w-full"
        height="560"
        loading="lazy"
        :src="drupalImage || cantoImage"
        :alt="`${firstName} ${lastName} - ${subtitle}`"
      />
    </div>

    <h3
      class="title mt-size-6 is-size-5 tablet:!text-size-5 desktop:!text-size-4 belowTablet:mt-size-7"
    >
      {{ firstName }} {{ lastName }}
    </h3>
    <p
      v-if="showPosition && subtitle"
      class="subtitle mt-2 mb-0 is-size-6 tablet:!text-size-6 desktop:!text-size-5"
    >
      <span>{{ subtitle }}</span>
    </p>

    <div
      class="employee-teaser-links mt-size-7 flex flex-col flex-wrap gap-1 is-size-5 belowTablet:mt-size-7"
    >
      <div class="flex gap-2">
        <!-- Mail icon -->
        <NuxtLink
          v-if="showMailShort && contactUrl?.path"
          class="link with-icon is-grey"
          :to="contactUrl.path"
          :title="$texts('employee.sendMail')"
        >
          <SpriteSymbolInline name="email" />
          <span class="sr-only">{{
            $texts('employee.sendMail')
          }}</span>
        </NuxtLink>

        <!-- Phone icon -->
        <a
          v-if="showPhoneShort && phoneLink"
          class="link with-icon is-grey"
          :href="phoneLink"
          :title="$texts('employee.call')"
        >
          <SpriteSymbolInline name="phone" />
          <span class="sr-only">{{ $texts('employee.call') }}</span>
        </a>

        <!-- Linkedin icon -->
        <a
          v-if="showLinkedinShort && linkedinLink"
          class="link with-icon is-grey"
          :href="linkedinLink"
          target="_blank"
          :title="$texts('employee.connectLinkedin')"
        >
          <SpriteSymbolInline name="linkedin" />
          <span class="sr-only">{{
            $texts('employee.connectLinkedin')
          }}</span>
        </a>

        <a
          v-if="xingLink"
          class="link with-icon is-grey"
          :href="xingLink"
          target="_blank"
          :title="$texts('employee.connectXing')"
        >
          <SpriteSymbolInline name="xing" />
          <span class="sr-only">{{
            $texts('employee.connectXing')
          }}</span>
        </a>

        <a
          v-if="twitterLink"
          class="link with-icon is-grey"
          :href="twitterLink"
          target="_blank"
          :title="$texts('employee.connectTwitter')"
        >
          <SpriteSymbolInline name="twitter" />
          <span class="sr-only">{{
            $texts('employee.connectTwitter')
          }}</span>
        </a>
      </div>

      <!-- Mail address (long) -->
      <div class="mt-size-8 flex flex-col gap-1">
        <NuxtLink
          v-if="showMailLong && contactUrl?.path"
          class="link"
          :to="contactUrl.path"
        >
          {{ $texts('employee.sendMail') }}
        </NuxtLink>

        <!-- Phone number (long) -->
        <a v-if="showPhoneLong && phoneLink" class="link" :href="phoneLink">
          {{ phone }}
        </a>

        <!-- Linkedin link (long) -->
        <a
          v-if="showLinkedinLong && linkedinLink"
          class="link"
          :href="linkedinLink"
          target="_blank"
        >
          {{ $texts('employee.connectLinkedin') }}
        </a>
      </div>
    </div>
  </article>
</template>

<script lang="ts" setup>
import { getImageStyleUrl, getBaseUrl } from '~/helpers/responsiveStyles'
import { getCantoImageStyle } from '~/helpers/canto'
import type { NodeEmployeeTeaserFragment } from '#graphql-operations'
import { notNullish } from '@vueuse/core'

const props = withDefaults(
  defineProps<
    NodeEmployeeTeaserFragment & {
      showPosition?: boolean
      showPhoneShort?: boolean
      showPhoneLong?: boolean
      showMailShort?: boolean
      showMailLong?: boolean
      showLinkedinShort?: boolean
      showLinkedinLong?: boolean
    }
  >(),
  {
    showPosition: false,
    showPhoneShort: false,
    showPhoneLong: false,
    showMailShort: true,
    showMailLong: false,
    showLinkedinShort: true,
    showLinkedinLong: false,
  },
)

const { $texts } = useEasyTexts()

const media = computed(() => props.portrait)

const drupalImage = computed(() => {
  if (media.value && 'fileUrl' in media.value) {
    const url = getBaseUrl(media.value?.fileUrl?.path)
    const hash = media.value?.changed?.toString() || ''
    if (url) {
      return getImageStyleUrl('employee_portrait', url, hash)
    }
  }
  return ''
})

const cantoImage = computed(() =>
  media.value && 'assetId' in media.value
    ? getCantoImageStyle(media.value.assetId!, {
        width: 560,
        height: 560,
      }).url
    : '',
)

const subtitle = computed(() =>
  (props.position || [])
    .map((v) => v?.name)
    .filter(notNullish)
    .join(','),
)

const phoneLink = computed(() => (props.phone ? `tel:${props.phone}` : ''))

const xingLink = computed(() =>
  props.xing ? `https://www.xing.com/profile/${props.xing}` : '',
)

const linkedinLink = computed(() =>
  props.linkedin ? `https://www.linkedin.com/in/${props.linkedin}` : '',
)

const twitterLink = computed(() =>
  props.twitter ? `https://www.twitter.com/${props.twitter}` : '',
)
</script>

<style>
@reference '~/assets/css/tailwind.css';
.employee-teaser {
  .employee-teaser-links {
    .with-icon {
      svg {
        @apply h-4 w-auto tablet:h-5;
      }
    }
    .link,
    span {
      @apply text-xs leading-none tablet:text-sm;
    }
  }
}
</style>
